import React from "react";

const Pricing = () => {
  const tiers = [
    {
      name: "pro",
      price: "₹3999",
      period: "/month",
      description: "Perfect for individual lawyers and small practices",
      features: [
        "Private Shared Server",
        "Document Management",
        "Case Calendar",
        "Basic Document AI Features",
        "Email Support",
        "Standard Security",
        "5GB Storage",
        "Monthly Backups",
      ],
      buttonText: "Get Started",
      isPopular: false,
    },
  ];

  return (
    <div className="h-[92vh] bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        {/* Header Section */}
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-orange-600 mb-1">
            Choose Your Plan
          </h1>
          <p className="text-base text-gray-600">
            Select the perfect plan for your legal practice
          </p>
        </div>

        {/* Main Content Container */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Pricing Cards */}
          <div className="flex justify-center gap-4 flex-1">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-xl ${
                  tier.isPopular
                    ? "border-2 border-orange-500 shadow-xl"
                    : "border border-gray-200 shadow-lg"
                } bg-white p-4 flex flex-col justify-between transition-transform duration-300 hover:scale-[1.02]`}
              >
                {tier.isPopular && (
                  <span className="absolute top-0 right-4 -translate-y-1/2 bg-gradient-to-r from-orange-400 to-orange-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                )}

                <div>
                  <div className="mb-2">
                    <h2 className="text-xl font-bold text-gray-900">
                      {tier.name}
                    </h2>
                    <div className="mt-1 flex items-baseline">
                      <span className="text-2xl font-bold text-orange-600">
                        {tier.price}
                      </span>
                      <span className="text-gray-500 ml-1">{tier.period}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      {tier.description}
                    </p>
                  </div>

                  <ul className="space-y-2 mb-4">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center">
                        <svg
                          className="h-4 w-4 text-orange-500 mr-2 flex-shrink-0"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  className={`w-full py-2 px-4 rounded-full text-center font-semibold transition-all duration-200 ${
                    tier.isPopular
                      ? "bg-gradient-to-r from-orange-400 to-orange-600 text-white hover:from-orange-500 hover:to-orange-700"
                      : "bg-white text-orange-500 border-2 border-orange-500 hover:bg-orange-50"
                  }`}
                >
                  {tier.buttonText}
                </button>
              </div>
            ))}

            {/* Custom Build Card */}
            <div className="relative rounded-xl border border-gray-200 shadow-lg bg-white p-4 flex flex-col justify-between transition-transform duration-300 hover:scale-[1.02]">
              <div>
                <div className="mb-2">
                  <h2 className="text-xl font-bold text-gray-900">
                    Custom Build
                  </h2>
                  <div className="mt-1">
                    <span className="text-2xl font-bold text-orange-600">
                      Custom Pricing
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    Tailored solutions for enterprise legal firms
                  </p>
                </div>

                <ul className="space-y-2 mb-4">
                  {[
                    "Dedicated Server",
                    "Custom AI Features",
                    "Priority Support",
                    "Advanced Security",
                    "Unlimited Storage",
                    "Daily Backups",
                    "Custom Integrations",
                    "Training & Onboarding",
                  ].map((feature) => (
                    <li key={feature} className="flex items-center">
                      <svg
                        className="h-4 w-4 text-orange-500 mr-2 flex-shrink-0"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button className="w-full py-2 px-4 rounded-full text-center font-semibold transition-all duration-200 bg-white text-orange-500 border-2 border-orange-500 hover:bg-orange-50">
                Contact Us
              </button>
            </div>
          </div>

          {/* Bottom Features Section */}
          <div className="grid md:grid-cols-3 gap-8 mt-6 mb-8">
            {[
              {
                title: "30-Day Money-Back",
                description: "Risk-free guarantee",
                icon: (
                  <svg
                    className="w-8 h-8 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
              },
              {
                title: "24/7 Support",
                description: "Always available",
                icon: (
                  <svg
                    className="w-8 h-8 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
              },
              {
                title: "Regular Updates",
                description: "Latest features",
                icon: (
                  <svg
                    className="w-8 h-8 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                ),
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-xl p-6 shadow-md border border-orange-200 hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center"
              >
                <div className="bg-orange-50 rounded-full p-4">{item.icon}</div>
                <h4 className="text-lg font-semibold text-orange-600 mt-4 mb-2">
                  {item.title}
                </h4>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
